import mergeWith from 'lodash/mergeWith'
import { type PartialDeep } from 'type-fest'
import { type ActionContext } from 'vuex'

import { type CoreModule } from '@/lib/store/types'
import { logError } from '@/logger'
import { removeObserver, reorder } from '@/utils/helpers'

import {
  type BlockCore,
  type BuilderState,
  type ResourceCore,
} from '../../types'
import { useBuilder } from '../useBuilder'

export const resourceSectionReorder = async <
  TResource extends ResourceCore,
  TBlock extends BlockCore,
>(
  {
    state,
    commit,
    dispatch,
  }: ActionContext<BuilderState<TResource, TBlock>, CoreModule>,
  { oldIndex, newIndex }: { oldIndex: number; newIndex: number }
): Promise<void> => {
  const currentResource = state._builder_currentState?.resource

  if (!currentResource) {
    logError('Unable to reorder sections of undefined resource')
  }

  const moduleName = state.moduleName

  const builderModule = useBuilder(moduleName)

  const createResourceCommand = builderModule.createResourceCommand<TResource>({
    commit: async (currentData, ctx) => {
      return mergeWith(currentData, ctx.update, (_objValue, srcValue, key) => {
        // Overwrite `sections` (array op) rather than merging
        if (key === 'sections') {
          return srcValue
        }
      })
    },
  })

  const sanitizedResource = removeObserver(currentResource) as TResource

  await createResourceCommand({
    update: {
      sections: reorder(sanitizedResource.sections, oldIndex, newIndex),
    } as unknown as PartialDeep<TResource>,
  })
}
